import { Evaluator, EvaluatorReturnType, Valence } from "../../types/app/evaluator";
import React from "react";
import { SEPARATOR } from "@components/llama/Filters/FilterEntitySelector";
import { Equals } from "@phosphor-icons/react/dist/ssr";

export type FilterType =
  | "text"
  | "number"
  | "boolean"
  | "categorical"
  | "categorical_multi"
  | "nested"
  | "key_value"
  | "date";

interface BaseFilter {
  target: BaseTarget; // The target of the filter (e.g. Evaluator).
  operator: Operator; // The operator of the filter (e.g. 'is' or 'has error').
  value: any; // The value of the filter (e.g. '100' or 'good').

  // Targets that can be used to replace the current target in the filter chip.
  // For example, if your target is 'Cost Evaluator', replacementTargets is a list of all other Evalutor targets.
  replacementTargets?: FilterTarget[];
}

export type Operator = {
  label: string;
  name: string;
  code: string;
  icon?: React.ReactNode;
};

export type FilterTarget = BaseTarget | NestedTarget;

export type BaseTarget = {
  label: string;
  target: string | Evaluator;
  type: FilterType;
  render?: (target: any) => React.ReactNode; // How to render this target in the dropdown menu.
  icon?: React.ReactNode; // If render is not provided, the dropdown will display icon + label
  errorState: boolean; // Whether target can have error state
  entity?: "inputs" | "evaluator"; // Used to serialise filter into be request.
  // alias is used as a searchable alternative name for the option.
  // it won't be displayed, but search will be performed on it.
  options?: { valence?: Valence; name: string; alias?: string }[]; // Options for categorical target.
  renderOption?: (option: any) => React.ReactNode; // How to render options in the dropdown.
};

// Dummy target used to render a horizontal line (separation line) in the dropdown menu.
export const SeparatorTarget: BaseTarget = {
  label: SEPARATOR,
  target: SEPARATOR,
  errorState: false,
  type: "text" as FilterType,
};

export type CategoricalFilter = BaseFilter & {
  selectedOption?: string;
  options?: { valence?: Valence; name: string }[];
  render?: (option: any) => React.ReactNode;
  type: "categorical" | "categorical_multi";
};

export type NestedTarget = BaseTarget & {
  type: "nested";
  childTargets: BaseTarget[];
  render?: (target: any) => React.ReactNode;
  icon?: React.ReactNode;
};

export type TextFilter = BaseFilter & {
  type: "text";
};

export type NumberFilter = BaseFilter & {
  type: "number";
};

export type BooleanFilter = BaseFilter & {
  type: "boolean";
};

export type DateFilter = BaseFilter & {
  type: "date";
};

export type NestedFilter = BaseFilter & {
  type: "nested";
};

/** This filter where user has to specify both key and value.
 * For example, a metadata filter where the user specifies the metadata key and value.*/
export type KeyValueFilter = BaseFilter & {
  type: "key_value";
  value: { key: string | null; value: string | null } | null;
};

export type Filter =
  | TextFilter
  | NumberFilter
  | BooleanFilter
  | CategoricalFilter
  | NestedFilter
  | KeyValueFilter
  | DateFilter;

export const EvaluatorReturnTypeToFilterType = (returnType: EvaluatorReturnType): FilterType => {
  switch (returnType) {
    case "text":
      return "text";
    case "number":
      return "number";
    case "select":
      return "categorical";
    case "multi_select":
      // TODO: this should be categorical_multi, but be for review tab filtering needs to support it first
      return "categorical";
    case "boolean":
      return "boolean";
    default:
      return "text";
  }
};

export const isNestedTarget = (target: FilterTarget): target is NestedTarget => target.type === "nested";
export const isCategoricalTarget = (target: FilterTarget): target is BaseTarget & { type: "categorical" } =>
  target.type === "categorical" || target.type === "categorical_multi";
export const isKeyValueFilter = (filter: Filter): filter is KeyValueFilter => filter.type === "key_value";
export const isDateFilter = (filter: Filter): filter is DateFilter => filter.type === "date";
export const getDefaultOperatorForTarget = (target: BaseTarget): Operator => {
  if (isNestedTarget(target)) return getOperatorsForTarget(target.childTargets[0])[0];
  return getOperatorsForTarget(target)[0];
};

// // Dummy operator used to render a horizontal line (separation line) in the dropdown menu
export const SeparatorOperator: Operator = {
  label: SEPARATOR,
  name: SEPARATOR,
  code: "__ignore",
};

export const EqualsOperator: Operator = {
  label: "equals",
  name: "equals",
  code: "eq",
  icon: React.createElement(Equals, { size: 16 }),
};

export const IsOperator: Operator = {
  label: "is",
  name: "is",
  code: "eq",
};

export const NotEqualsOperator: Operator = {
  label: "not equals",
  name: "not equals",
  code: "ne",
};

export const IsNotOperator: Operator = {
  label: "not",
  name: "not",
  code: "ne",
};

export const ContainsOperator: Operator = {
  label: "contains",
  name: "contains",
  code: "co",
};

export const NotContainsOperator: Operator = {
  label: "not contains",
  name: "not contains",
  code: "nc",
};

// For Categorical filters we chose to use verb 'is' to indicate that target is one of [value1, value2...]
// on backend this behavior corresponds to 'contains' operator, hence this operator looks like 'is', but behaves like 'contains'.
export const ContainsButLooksLikeIsOperator: Operator = {
  label: "is",
  name: "is",
  code: "co",
};

export const NotContainsButLooksLikeIsOperator: Operator = {
  label: "not",
  name: "not",
  code: "nc",
};

export const GreaterThanOperator: Operator = {
  label: "greater than",
  name: "greater than",
  code: "gt",
};

export const LessThanOperator: Operator = {
  label: "less than",
  name: "less than",
  code: "lt",
};

export const HasErrorOperator: Operator = {
  label: "has error",
  name: "has error",
  code: "er",
};

export const NoErrorOperator: Operator = {
  label: "no error",
  name: "no error",
  code: "ok",
};

export const getOperatorsForTarget = (target: BaseTarget): Operator[] => {
  let extraOperators: Operator[] = [];
  if (target.errorState === true) {
    extraOperators = [SeparatorOperator, HasErrorOperator, NoErrorOperator];
  }

  switch (target.type) {
    case "text":
      return [IsOperator, IsNotOperator, ContainsOperator, NotContainsOperator, ...extraOperators];
    case "categorical":
      return [ContainsButLooksLikeIsOperator, NotContainsButLooksLikeIsOperator, ...extraOperators];
    case "categorical_multi":
      return [ContainsButLooksLikeIsOperator, NotContainsButLooksLikeIsOperator, ...extraOperators];
    case "number":
      return [EqualsOperator, NotEqualsOperator, GreaterThanOperator, LessThanOperator, ...extraOperators];
    case "boolean":
      return [IsOperator, IsNotOperator, ...extraOperators];
    case "key_value":
      return [ContainsOperator, NotContainsOperator, ...extraOperators];
    case "date":
      return [IsOperator, ...extraOperators];
    default:
      return [EqualsOperator, NotEqualsOperator, ...extraOperators];
  }
};

export const operatorDoesNotRequireValue = (operator: Operator) => {
  return operator.name === "has error" || operator.name === "no error";
};
